import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message } from 'antd';
import api from '../services/api';
import PageSkeleton from '../components/Skeleton';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const CATEGORY_KEYS = ['merchant.catWatches', 'merchant.catJewelry', 'merchant.catBags', 'merchant.catAccessories', 'merchant.catClothing'];

export default function MerchantApply() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', contact_name: '', contact_phone: '',
    license_no: '', description: '', categories: [],
  });

  useEffect(() => {
    api.get('/merchants/my')
      .then(r => {
        setMerchant(r.data);
        if (r.data && r.data.status === 'rejected') {
          setForm({
            name: r.data.name || '',
            contact_name: r.data.contact_name || '',
            contact_phone: r.data.contact_phone || '',
            license_no: r.data.license_no || '',
            description: r.data.description || '',
            categories: r.data.categories || [],
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleCat = (cat) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return message.warning(t('common.required'));
    if (!form.contact_phone.trim()) return message.warning(t('common.required'));
    if (form.categories.length === 0) return message.warning(t('common.required'));

    setSubmitting(true);
    try {
      await api.post('/merchants/apply', form);
      message.success(t('merchant.applySuccess'));
      const r = await api.get('/merchants/my');
      setMerchant(r.data);
    } catch (err) {
      message.error(err.response?.data?.error || t('common.failed'));
    } finally { setSubmitting(false); }
  };

  if (loading) return <PageSkeleton />;

  // Already active — redirect to dashboard
  if (merchant?.status === 'active') {
    return (
      <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <Header navigate={navigate} t={t} />
          <div style={{ margin: '40px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: TEAL, marginBottom: 8 }}>{t('merchant.statusApproved')}</div>
            <div style={{ fontSize: 14, color: MUTED, marginBottom: 24 }}>{merchant.name}</div>
            <button onClick={() => navigate('/merchant/dashboard')} style={{
              padding: '12px 32px', borderRadius: 8, border: 'none',
              background: GOLD, color: '#fff', fontSize: 14, cursor: 'pointer',
            }}>{t('merchant.dashboard')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Pending review
  if (merchant?.status === 'pending') {
    return (
      <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <Header navigate={navigate} t={t} />
          <div style={{ margin: '40px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: GOLD, marginBottom: 8 }}>{t('merchant.statusPending')}</div>
            <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.8 }}>
              {t('merchant.pendingReviewDesc', { name: merchant.name })}<br/>
              {t('merchant.pendingReviewTime')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rejected — show form with reason
  const isRejected = merchant?.status === 'rejected';

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <Header navigate={navigate} t={t} />

        {/* Banner */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 14, overflow: 'hidden',
          background: 'linear-gradient(135deg, #2A1A0A 0%, #3A2A1A 50%, #2A1A0A 100%)',
          padding: '28px 24px', position: 'relative',
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{t('merchant.apply')}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.8 }}>
            {t('merchant.applyBannerLine1')}<br/>
            {t('merchant.applyBannerLine2')}
          </div>
        </div>

        {/* Rejected warning */}
        {isRejected && (
          <div style={{
            margin: '16px 16px 0', padding: '14px 16px', borderRadius: 8,
            background: '#FFF5F5', border: '1px solid #FED7D7',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#C0392B', marginBottom: 4 }}>{t('merchant.statusRejected')}</div>
            <div style={{ fontSize: 12, color: '#E74C3C' }}>{merchant.reject_reason || t('merchant.statusRejected')}</div>
          </div>
        )}

        {/* Form */}
        <div style={{ margin: '16px 16px 0' }}>
          <FormField label={t('merchant.shopName')} required>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={t('merchant.shopName')} style={inputStyle} />
          </FormField>

          <FormField label={t('merchant.contact')}>
            <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
              placeholder={t('merchant.contact')} style={inputStyle} />
          </FormField>

          <FormField label={t('merchant.contact')} required>
            <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
              placeholder={t('auth.phonePlaceholder')} style={inputStyle} />
          </FormField>

          <FormField label={t('merchant.license')}>
            <input value={form.license_no} onChange={e => setForm({ ...form, license_no: e.target.value })}
              placeholder={t('common.optional')} style={inputStyle} />
          </FormField>

          <FormField label={t('merchant.products')} required>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CATEGORY_KEYS.map(catKey => {
                const sel = form.categories.includes(catKey);
                return (
                  <button key={catKey} onClick={() => toggleCat(catKey)} style={{
                    padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                    border: sel ? `1px solid ${GOLD}` : `1px solid ${BD}`,
                    background: sel ? `${GOLD}10` : '#fff',
                    color: sel ? GOLD : MUTED,
                  }}>{t(catKey)}</button>
                );
              })}
            </div>
          </FormField>

          <FormField label={t('merchant.shopDesc')}>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={t('merchant.shopDesc')} rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </FormField>
        </div>

        {/* Benefits */}
        <div style={{
          margin: '20px 16px 0', padding: '16px',
          background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('membership.benefits')}</div>
          {[
            { icon: '📸', text: t('merchant.products') },
            { icon: '💰', text: t('membership.feeDiscount') },
            { icon: '📊', text: t('merchant.revenue') },
            { icon: '🛡', text: t('landing.authentic') },
            { icon: '📅', text: t('merchant.orders') },
          ].map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: 13, color: MUTED }}>
              <span>{b.icon}</span><span>{b.text}</span>
            </div>
          ))}
        </div>

        {/* Submit */}
        <div style={{ margin: '24px 16px' }}>
          <button onClick={handleSubmit} disabled={submitting} style={{
            width: '100%', padding: '14px 0', borderRadius: 8, border: 'none',
            background: GOLD, color: '#fff', fontSize: 16, fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}>
            {submitting ? t('common.loading') : t('merchant.submitApply')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ navigate, t }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '14px 16px',
      background: '#fff', borderBottom: `1px solid ${BD}`,
    }}>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
      <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('merchant.apply')}</span>
      <div style={{ width: 30 }} />
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: INK, marginBottom: 6 }}>
        {label}{required && <span style={{ color: '#E74C3C', marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: `1px solid ${BD}`, fontSize: 14, color: INK,
  background: '#fff', outline: 'none', boxSizing: 'border-box',
};
